// app/src/modules/ui/AppHeader.tsx
// The single header shared across the whole app (till + back office + every page),
// so the chrome is identical everywhere. Layout: [Logo] ........ [Store][Back Office][Clock][Logout]
// Store = the till. Back Office = every other screen, grouped into section tabs + sub-tabs.
// — brand on the left; the switch, clock and sign-out grouped on the right. Sticky on every
// page; renders nothing on the login screen.
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import { getDevice } from "../../lib/device";
import BrandLockup from "./Brand";
import ThemeToggle from "./ThemeToggle";
import ConnectivityBadge from "./Connectivity";
import { WaBadge } from "./WhatsApp";
import { fmtDate, fmtTime } from "../../lib/datetime";

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  const time = fmtTime(now.getTime(), { hour: "2-digit", minute: "2-digit" });
  const date = fmtDate(now.getTime(), { weekday: "short", day: "numeric", month: "short" });
  return (
    <div className="app-clock">
      <span className="num app-clock-time">{time}</span>
      <span className="app-clock-date">{date}</span>
    </div>
  );
}

const IC = { width: 18, height: 18, fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" } as const;

export default function AppHeader() {
  const nav = useNavigate();
  const loc = useLocation();
  const { logout } = useSession();
  const device = getDevice();
  if (loc.pathname === "/login") return null;
  const onStore = loc.pathname === "/";
  return (
    <header className="app-header">
      <div className="app-header-brand">
        <BrandLockup size={58} />
        <span className="app-header-sub">{device.branch_code} · Till 1</span>
      </div>
      <div className="app-header-right">
        <nav className="app-header-tabs" aria-label="Store or back office">
          <button className={`app-tab${onStore ? " on" : ""}`} aria-current={onStore ? "page" : undefined} onClick={() => nav("/")}>
            <span className="app-tab-ic"><svg {...IC} viewBox="0 0 24 24"><path d="M3 9l1-5h16l1 5" /><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" /><path d="M5 11v10h14V11" /><path d="M10 21v-6h4v6" /></svg></span><span className="app-tab-txt">Store</span>
          </button>
          <button className={`app-tab${!onStore ? " on" : ""}`} aria-current={!onStore ? "page" : undefined} onClick={() => nav("/backoffice")}>
            <span className="app-tab-ic"><svg {...IC} viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg></span><span className="app-tab-txt">Back Office</span>
          </button>
        </nav>
        <Clock />
        <ConnectivityBadge />
        <WaBadge />
        <span className="app-header-theme"><ThemeToggle compact /></span>
        <button className="app-header-logout" onClick={async () => { await logout(); void nav("/login"); }} title="Log out" aria-label="Log out">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>
        </button>
      </div>
    </header>
  );
}
